"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { CheckCircle2, Phone, Mail, MapPin, Loader2, Calendar, DollarSign, Paintbrush, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  type,
  ...props
}, ref) => {
  return <input type={type} className={cn("flex h-12 w-full border-0 border-b border-input bg-transparent px-0 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/30 focus-visible:outline-none focus-visible:border-primary transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-50", className)} ref={ref} {...props} />;
});
Input.displayName = "Input";
const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({
  className,
  ...props
}, ref) => {
  return <textarea className={cn("flex min-h-[120px] w-full border-0 border-b border-input bg-transparent px-0 py-2 text-sm ring-offset-background placeholder:text-muted-foreground/30 focus-visible:outline-none focus-visible:border-primary transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-50 resize-y", className)} ref={ref} {...props} />;
});
Textarea.displayName = "Textarea";
const Select = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(({
  className,
  children,
  ...props
}, ref) => {
  return <div data-section-id="21382" className="relative">
        <select className={cn("flex h-12 w-full items-center justify-between border-0 border-b border-input bg-transparent px-0 py-2 text-sm ring-offset-background placeholder:text-muted-foreground/30 focus:outline-none focus:border-primary transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-50 appearance-none", className)} ref={ref} {...props}>
          {children}
        </select>
        <div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>;
});
Select.displayName = "Select";
function QuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center h-full min-h-[400px] p-8 text-center bg-card rounded-none border border-primary/20 shadow-xl animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-primary/10 text-primary rounded-none flex items-center justify-center mb-6">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <h3 className="text-2xl font-bold text-foreground mb-2 font-sans tracking-tighter">PROTOCOL INITIALIZED</h3>
        <p className="text-muted-foreground mb-8 max-w-md font-sans">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="min-w-[200px] rounded-none border-primary text-primary hover:bg-primary/10">
          SUBMIT NEW INQUIRY
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">FULL NAME <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <Input placeholder="ENTER FULL NAME" {...field} />
              </FormControl>
              <FormMessage className="text-[10px] font-mono" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">WORK EMAIL <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <Input type="email" placeholder="EMAIL@DOMAIN.COM" {...field} />
              </FormControl>
              <FormMessage className="text-[10px] font-mono" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required",
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">CONTACT NUMBER <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (000) 000-0000" {...field} />
              </FormControl>
              <FormMessage className="text-[10px] font-mono" />
            </FormItem>} />

        <FormField control={control} name="service_type" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">PARTNERSHIP TYPE <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <Select {...field} value={field.value || ""}>
                  <option value="" disabled className="bg-card">SELECT TYPE</option>
                  {["Infrastructure Access", "Capital Deployment", "Intelligence Integration", "Network Expansion", "Strategic Advisory", "Asset Management"].map(opt => <option key={opt} value={opt} className="bg-card">{opt}</option>)}
                </Select>
              </FormControl>
              <FormMessage className="text-[10px] font-mono" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="preferred_start_date" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">ESTIMATED TIMELINE</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input type="date" {...field} className="pl-10" />
                  <SafeHydrate><Calendar className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-primary pointer-events-none" /></SafeHydrate>
                </div>
              </FormControl>
              <FormDescription className="text-[10px] font-mono opacity-50">Specify target integration window.</FormDescription>
              <FormMessage className="text-[10px] font-mono" />
            </FormItem>} />

        <FormField control={control} name="budget" rules={{
        min: {
          value: 0,
          message: "Budget cannot be negative"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">ALLOCATION (USD)</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input type="number" placeholder="0.00" {...field} className="pl-10" />
                  <DollarSign className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-primary pointer-events-none" />
                </div>
              </FormControl>
              <FormMessage className="text-[10px] font-mono" />
            </FormItem>} />
      </div>

      <FormField control={control} name="project_details" rules={{
      required: "Project details are required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">PROPOSAL OVERVIEW <span className="text-primary">*</span></FormLabel>
            <FormControl>
              <Textarea placeholder="SPECIFY REQUIREMENTS, SCALE, AND STRATEGIC OBJECTIVES..." {...field} />
            </FormControl>
            <FormDescription className="text-[10px] font-mono opacity-50">Detailed protocols ensure faster verification and processing.</FormDescription>
            <FormMessage className="text-[10px] font-mono" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-none animate-in fade-in slide-in-from-top-2">
          <p className="text-destructive text-[10px] font-mono flex items-center gap-2">
            <span className="text-lg"></span> {submitError}
          </p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-sm font-bold rounded-none bg-primary text-primary-foreground shadow-none hover:bg-primary/90 transition-all duration-300 relative overflow-hidden group">
        <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_2s_infinite] pointer-events-none" />
        {isSubmitting ? <>
            <Loader2 className="animate-spin mr-2 size-5" />
            VERIFYING...
          </> : <>
            SUBMIT INQUIRY <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>
      
      <p className="text-[10px] font-mono text-center text-muted-foreground/50 mt-4 uppercase tracking-tighter">
        SUBMISSION CONSTITUTES AGREEMENT TO INSTITUTIONAL PRIVACY PROTOCOLS.
      </p>
    </Form>;
}
export default function GetAFreeQuote() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  const benefits = [{
    title: "Infrastructure Audit",
    description: "Deep-layer analysis of system compatibility and scale."
  }, {
    title: "Capital Efficiency",
    description: "Optimized allocation models for maximum network yield."
  }, {
    title: "Intelligence Access",
    description: "Direct integration with the N2N proprietary engine."
  }];
  return;
}